import { useContext, useState, useEffect, useRef } from "react";
import Sidebar from "../components/sidebar/Sidebar.jsx";
import Header from "../components/header/Header.jsx";
import PageTitle from "../components/PageTitle.jsx";
import Spinner from "../components/Spinner.jsx";
import { useRequireLoginEffect } from "../components/auth/useRequireLoginEffect.js";
import { GlobalContext } from "../utils/context/GlobalContextProvider.jsx";
import AppAlerts from "./AppAlerts.jsx";

export default function WideSecureLayout({ title, actions, children, alerts = [], titleContainerClassName = '' }) {
    const { context } = useContext(GlobalContext);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const layoutRef = useRef(null);
    const titleBarRef = useRef(null);
    const stickyWrapRef = useRef(null);

    useRequireLoginEffect();

    useEffect(() => {
        const layoutElement = layoutRef.current;
        const titleBarElement = titleBarRef.current;

        const handleScroll = () => {
            const scrollTop = layoutElement.scrollTop;

            const headerElement = document.querySelector('.navbar');
            const headerHeight = headerElement ? headerElement.offsetHeight : 0;
            const titleBarHeight = titleBarElement ? titleBarElement.offsetHeight : 0;
            const scrollThreshold = titleBarHeight + (headerHeight / 2);

            if (scrollTop > scrollThreshold && !isSticky) {
                setIsSticky(true);
            } else if (scrollTop <= scrollThreshold && isSticky) {
                setIsSticky(false);
            }
        };

        if (layoutElement) {
            layoutElement.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (layoutElement) {
                layoutElement.removeEventListener("scroll", handleScroll);
            }
        };
    }, [isSticky]);

    const containerClass = 'grow-0 mx-auto';
    const scrollableContainerClass = 'lg:pl-8 flex-1 mx-auto max-w-full relative';

    const baseTitleBarClasses =
        'title-bar flex flex-col justify-between text-center items-center ' +
        'transition-all ' +
        'bg-gray-100 dark:bg-gray-900 ' +
        'md:flex-row md:text-left';

    const nonStickyTitleBarClasses = 'pt-8';

    const stickyTitleBarClasses =
        'sticky flex top-16 py-2 md:py-3 z-10 transition-all shadow-md';

    return (
        <div className="flex h-[100dvh] overflow-hidden">
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

            <div ref={layoutRef} className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <main className="layout-wide grow px-4 sm:px-6 lg:px-8">
                    {/* Sticky bar */}
                    <div className={`${baseTitleBarClasses} ${isSticky ? stickyTitleBarClasses : 'hidden'}`}>
                        {title && (<div className={`${containerClass} ${titleContainerClassName}`}>
                            <PageTitle textSizeClass={'hidden md:block text-xl'}>
                                {title}
                            </PageTitle>
                        </div>)}
                        {actions && <div className={`${scrollableContainerClass}`}>
                            <div className="my-auto flex justify-end">{actions ?? null}</div>
                        </div>}
                    </div>

                    {/* Normal Title-bar */}
                    <div ref={titleBarRef} className={`${baseTitleBarClasses} ${nonStickyTitleBarClasses} mb-8`}>
                        {title && (<div className={`${containerClass} ${titleContainerClassName} text-center`}>
                            <PageTitle textSizeClass={'flex justify-center text-2xl md:text-3xl mb-3 md:mb-0'}>
                                {title}
                            </PageTitle>
                        </div>)}
                        {actions && <div className={`${scrollableContainerClass}`}>
                            <div className="my-auto flex justify-end">{actions ?? null}</div>
                        </div>}
                    </div>

                    <AppAlerts alerts={alerts} />

                    <div className={`${containerClass} pb-8`}>
                        {context.currentIdentity === null ? <Spinner/> : children}
                    </div>
                </main>
            </div>
        </div>
    );
}
